/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable, inject } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi } from '@alfresco/js-api';
import { Observable, from } from 'rxjs';
import { PdfAnnot, PdfField, PdfRect } from './pdf-studio.models';

type PdfLib = typeof import('pdf-lib');

/**
 * The pdf-lib engine behind the studio.
 *
 * Every change the tools describe — page surgery, drawn marks, form fields —
 * is applied to a real PDFDocument here, so what Save writes is a genuine PDF
 * and never a picture of one. Text is rasterised through a canvas before it
 * is embedded: pdf-lib's built-in fonts cannot encode Thai, but the browser's
 * canvas fonts can, so every script renders exactly as the editor showed it.
 */
@Injectable({ providedIn: 'root' })
export class PdfStudioService {
  private readonly api = inject(AlfrescoApiService);

  private pdfLib: Promise<PdfLib> | null = null;

  /** pdf-lib, fetched the first time an edit actually needs it. */
  lib(): Promise<PdfLib> {
    this.pdfLib ??= import('pdf-lib');
    return this.pdfLib;
  }

  /** The editable document the page tools mutate. */
  async loadDoc(bytes: Uint8Array): Promise<import('pdf-lib').PDFDocument> {
    const lib = await this.lib();
    return lib.PDFDocument.load(bytes.slice(), { ignoreEncryption: true, updateMetadata: false });
  }

  // ---- page operations ----------------------------------------------------

  /** A blank page inserted at `index`, sized like its neighbour. */
  insertBlankPage(doc: import('pdf-lib').PDFDocument, index: number): void {
    const near = doc.getPage(Math.min(index, doc.getPageCount() - 1)) ?? doc.getPage(0);
    const { width, height } = near.getSize();
    doc.insertPage(index, [width, height]);
  }

  deletePage(doc: import('pdf-lib').PDFDocument, index: number): void {
    if (doc.getPageCount() <= 1) {
      return;
    }
    doc.removePage(index);
  }

  /** Reorders by detaching the page and inserting it at the new index. */
  movePage(doc: import('pdf-lib').PDFDocument, fromIndex: number, toIndex: number): void {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= doc.getPageCount() || toIndex >= doc.getPageCount()) {
      return;
    }
    const page = doc.getPage(fromIndex);
    doc.removePage(fromIndex);
    doc.insertPage(toIndex, page);
  }

  async rotatePage(doc: import('pdf-lib').PDFDocument, index: number, deltaDeg = 90): Promise<void> {
    const lib = await this.lib();
    const page = doc.getPage(index);
    page.setRotation(lib.degrees((page.getRotation().angle + deltaDeg + 360) % 360));
  }

  cropPage(doc: import('pdf-lib').PDFDocument, index: number, rect: PdfRect): void {
    doc.getPage(index).setCropBox(rect.x, rect.y, rect.w, rect.h);
  }

  /** Swaps `index` for page `srcIndex` of another PDF. */
  async replacePage(doc: import('pdf-lib').PDFDocument, index: number, srcBytes: Uint8Array, srcIndex = 0): Promise<void> {
    const lib = await this.lib();
    const src = await lib.PDFDocument.load(srcBytes.slice(), { ignoreEncryption: true });
    const [copied] = await doc.copyPages(src, [Math.min(srcIndex, src.getPageCount() - 1)]);
    doc.insertPage(index, copied);
    doc.removePage(index + 1);
  }

  /** One page as a standalone file — the "extract" download. */
  async extractPage(doc: import('pdf-lib').PDFDocument, index: number): Promise<Uint8Array> {
    const lib = await this.lib();
    const single = await lib.PDFDocument.create();
    const [copied] = await single.copyPages(doc, [index]);
    single.addPage(copied);
    return single.save();
  }

  async saveBytes(doc: import('pdf-lib').PDFDocument): Promise<Uint8Array> {
    return doc.save();
  }

  // ---- annotations & fields ----------------------------------------------

  /**
   * Draws every annotation and creates every form field. Marks that fail —
   * an image it cannot embed, a field whose name collides — are skipped
   * rather than failing the whole save.
   */
  async burn(doc: import('pdf-lib').PDFDocument, annots: PdfAnnot[], fields: PdfField[]): Promise<void> {
    const lib = await this.lib();

    for (const annot of annots) {
      try {
        const page = doc.getPage(annot.page);
        await this.burnOne(lib, doc, page, annot);
      } catch {
        // one broken mark must not sink the save
      }
    }

    if (fields.length) {
      const form = doc.getForm();
      const font = await doc.embedFont(lib.StandardFonts.Helvetica);
      for (const field of fields) {
        try {
          const page = doc.getPage(field.page);
          const rect = { x: field.rect.x, y: field.rect.y, width: field.rect.w, height: field.rect.h };
          if (field.kind === 'text') {
            form.createTextField(field.name).addToPage(page, rect);
          } else if (field.kind === 'check') {
            form.createCheckBox(field.name).addToPage(page, rect);
          } else if (field.kind === 'radio') {
            form.createRadioGroup(field.name).addOptionToPage(field.options?.[0] ?? 'option', page, rect);
          } else {
            const dropdown = form.createDropdown(field.name);
            dropdown.addOptions(field.options ?? []);
            dropdown.addToPage(page, rect);
          }
        } catch {
          // duplicate field names or a page that vanished — skip the field
        }
      }
      form.updateFieldAppearances(font);
    }
  }

  private async burnOne(lib: PdfLib, doc: import('pdf-lib').PDFDocument, page: import('pdf-lib').PDFPage, a: PdfAnnot): Promise<void> {
    const color = hexRgb(lib, a.color ?? '#e53935');

    switch (a.kind) {
      case 'highlight':
        for (const r of a.rects ?? [a]) {
          page.drawRectangle({ x: r.x, y: r.y, width: r.w, height: r.h, color: hexRgb(lib, '#ffeb3b'), opacity: 0.4 });
        }
        break;
      case 'underline':
      case 'strike':
        for (const r of a.rects ?? [a]) {
          const y = a.kind === 'underline' ? r.y : r.y + r.h / 2;
          page.drawLine({ start: { x: r.x, y }, end: { x: r.x + r.w, y }, thickness: 0.8, color });
        }
        break;
      case 'ink': {
        const pts = a.points ?? [];
        for (let i = 1; i < pts.length; i++) {
          page.drawLine({ start: pts[i - 1], end: pts[i], thickness: 1.6, color });
        }
        break;
      }
      case 'rect':
        page.drawRectangle({ x: a.x, y: a.y, width: a.w, height: a.h, borderColor: color, borderWidth: 1.5 });
        break;
      case 'ellipse':
        page.drawEllipse({ x: a.x + a.w / 2, y: a.y + a.h / 2, xScale: a.w / 2, yScale: a.h / 2, borderColor: color, borderWidth: 1.5 });
        break;
      case 'line':
        page.drawLine({ start: { x: a.x, y: a.y }, end: { x: a.x + a.w, y: a.y + a.h }, thickness: 1.5, color });
        break;
      case 'arrow': {
        page.drawLine({ start: { x: a.x, y: a.y }, end: { x: a.x + a.w, y: a.y + a.h }, thickness: 1.5, color });
        const angle = Math.atan2(a.h, a.w);
        const head = 8;
        for (const d of [2.6, -2.6]) {
          page.drawLine({
            start: { x: a.x + a.w, y: a.y + a.h },
            end: { x: a.x + a.w + head * Math.cos(angle + d), y: a.y + a.h + head * Math.sin(angle + d) },
            thickness: 1.5,
            color
          });
        }
        break;
      }
      case 'redact':
        page.drawRectangle({ x: a.x, y: a.y, width: a.w, height: a.h, color: hexRgb(lib, '#000000') });
        break;
      case 'cover':
        page.drawRectangle({ x: a.x, y: a.y, width: a.w, height: a.h, color: hexRgb(lib, '#ffffff') });
        await this.burnText(doc, page, a, false);
        break;
      case 'stamp': {
        page.drawRectangle({ x: a.x, y: a.y, width: a.w, height: a.h, borderColor: color, borderWidth: 2 });
        await this.burnText(doc, page, a, true);
        break;
      }
      case 'note': {
        // a sticky-note square plus the comment beside it in small print
        page.drawRectangle({
          x: a.x,
          y: a.y,
          width: 12,
          height: 12,
          color: hexRgb(lib, '#fdd835'),
          borderColor: hexRgb(lib, '#f9a825'),
          borderWidth: 0.8
        });
        if (a.text) {
          const note = { ...a, x: a.x + 16, y: a.y + 12 };
          await this.burnText(doc, page, note, false, 8);
        }
        break;
      }
      case 'image':
        await this.burnImage(doc, page, a);
        break;
      case 'textbox':
      default:
        await this.burnText(doc, page, a, a.kind === 'textbox');
        break;
    }
  }

  /**
   * Text as an embedded PNG — the only route that renders Thai (and every
   * other script the browser can draw) exactly right.
   */
  private async burnText(
    doc: import('pdf-lib').PDFDocument,
    page: import('pdf-lib').PDFPage,
    a: PdfAnnot,
    centered: boolean,
    fontSize = 14
  ): Promise<void> {
    if (!a.text?.trim()) {
      return;
    }
    const img = await rasterizeText(a.text, { fontSize, color: a.color ?? '#1a1a1a', maxWidth: centered ? a.w : undefined });
    const png = await doc.embedPng(img.bytes);
    const scale = Math.min(centered ? (a.w - 8) / img.w : 1, centered ? (a.h - 8) / img.h : 1);
    const w = img.w * scale;
    const h = img.h * scale;
    const x = centered ? a.x + (a.w - w) / 2 : a.x;
    const y = centered ? a.y + (a.h - h) / 2 : a.y - h; // `a.y` is the top edge
    page.drawImage(png, { x, y, width: w, height: h });
  }

  private async burnImage(doc: import('pdf-lib').PDFDocument, page: import('pdf-lib').PDFPage, a: PdfAnnot): Promise<void> {
    if (!a.image) {
      return;
    }
    const embedded = a.image.kind === 'png' ? await doc.embedPng(a.image.bytes) : await doc.embedJpg(a.image.bytes);
    page.drawImage(embedded, { x: a.x, y: a.y, width: a.w, height: a.h });
  }

  // ---- repository ---------------------------------------------------------

  /** Writes the edited file back as a new version of the same node. */
  saveAsNewVersion(nodeId: string, name: string, bytes: Uint8Array, major: boolean, comment: string): Observable<any> {
    const file = new File([bytes.slice().buffer as ArrayBuffer], name, { type: 'application/pdf' });
    return from(new NodesApi(this.api.getInstance() as any).updateNodeContent(nodeId, file as any, { majorVersion: major, comment }) as Promise<any>);
  }
}

// ---- helpers ----------------------------------------------------------------

function hexRgb(lib: PdfLib, hex: string): ReturnType<PdfLib['rgb']> {
  const h = hex.replace('#', '');
  return lib.rgb(parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255);
}

interface RasterisedText {
  bytes: Uint8Array;
  /** Size in PDF points (the canvas is 3× for print sharpness). */
  w: number;
  h: number;
}

const TEXT_SCALE = 3;

/**
 * Draws text on a canvas and returns it as PNG bytes with its size in points.
 * Word-wraps when `maxWidth` is given.
 */
export async function rasterizeText(
  text: string,
  opts: { fontSize?: number; color?: string; maxWidth?: number; bold?: boolean } = {}
): Promise<RasterisedText> {
  const fontSize = opts.fontSize ?? 14;
  const px = fontSize * TEXT_SCALE;
  const font = `${opts.bold ? '700' : '400'} ${px}px "Segoe UI", "Noto Sans Thai", "Leelawadee UI", Tahoma, sans-serif`;
  const pad = 2 * TEXT_SCALE;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('This browser cannot draw text');
  }
  ctx.font = font;

  // wrap into lines
  const lines: string[] = [];
  const maxW = opts.maxWidth ? opts.maxWidth * TEXT_SCALE : Infinity;
  for (const raw of text.split('\n')) {
    let line = '';
    for (const word of raw.split(/\s+/)) {
      const trial = line ? `${line} ${word}` : word;
      if (ctx.measureText(trial).width > maxW && line) {
        lines.push(line);
        line = word;
      } else {
        line = trial;
      }
    }
    lines.push(line);
  }

  const lineH = px * 1.3;
  canvas.width = Math.ceil(Math.min(maxW, Math.max(...lines.map((l) => ctx.measureText(l).width), 1)) + pad * 2);
  canvas.height = Math.ceil(lines.length * lineH + pad * 2);
  ctx.font = font;
  ctx.fillStyle = opts.color ?? '#1a1a1a';
  ctx.textBaseline = 'top';
  lines.forEach((line, i) => ctx.fillText(line, pad, pad + i * lineH));

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) {
    throw new Error('The text could not be turned into a picture');
  }
  return { bytes: new Uint8Array(await blob.arrayBuffer()), w: canvas.width / TEXT_SCALE, h: canvas.height / TEXT_SCALE };
}
